import { useMemo } from 'react';
import env, { addDomain } from '@/env';
import { useConfig } from '@/apis/ducc';
import { getQuery, formatQueryObjNew } from '@/common/utils';

interface Params {
  pathname: string;
  query?: string;
  method?: 'GET' | 'POST';
  body?: any;
}

function getPerfomanceApi({ method = 'GET', pathname, query = '', body }: Params) {
  const options: any = {};
  options.method = method;
  options.mode = 'cors';
  options.credentials = 'include';

  if (body) {
    const headers: any = {};
    if (typeof body === 'string') {
      headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';

      options.body = body;
    } else {
      headers['Content-Type'] = 'application/json; charset=utf-8';

      options.body = JSON.stringify(body);
    }

    options.headers = headers;
  }

  // 使用pathname作为key将入参信息全部记录下来作为缓存是否可用的判断依据
  return {
    [pathname]: {
      pathname,
      method,
      url: addDomain(pathname + query, env.MAC_DOMAIN),
      query,
      body,
      res: fetch(addDomain(pathname + query, env.MAC_DOMAIN), options),
    },
  };
}

let prefetchApisUseable: string | null = '';
try {
  prefetchApisUseable = window.localStorage.getItem('acorePrefetchApiUsable');
} catch (ex) {
  console.log('报错', ex);
}

function usePerformanceApis(apis: any[] = [] /* 一定要保证入参apis不能经常变 */) {
  const [getConfig]: any = useConfig();

  const prefetchApis = useMemo(() => {
    return apis.reduce((total, cur: any) => {
      return {
        ...total,
        ...cur(),
      };
    }, {});
  }, [apis]);

  getConfig('prefetchApisUseable', false, (val: string | boolean) => {
    if (typeof val === 'boolean') return;
    if (val === prefetchApisUseable) return val;
    try {
      window.localStorage.setItem('acorePrefetchApiUsable', val);
    } catch (ex) {
      console.error('出错了', ex);
    }
    return val;
  });

  return {
    prefetchApis,
    // 防止功能出错使用开关控制，prefetchApis是否可以用
    prefetchApisUseable: prefetchApisUseable === '1',
  };
}

// ----------审核管理页---------------
// https://joyspace.jd.com/pages/9rdMWqF81Qct6eGnOcJH
function activityIsmanager() {
  const activityId = getQuery('activityId');
  if (!activityId) return {};

  const paramsObj = {
    activityId,
  };

  return getPerfomanceApi({
    method: 'POST',
    pathname: `/activity/ismanager`,
    body: formatQueryObjNew(paramsObj),
  });
}

// batchPage 同分配页，参数不同 {countType:2, onlineFlag: 1, checkWay: 1, page: 1, pageSize: 12}

// areaDetailV2 同报名页

// ----------分配页---------------
// areaDetailV2 同报名页

function batchPage(params = {}) {
  const activityId = getQuery('activityId');
  if (!activityId) return {};

  const blockId = getQuery('blockId') || '';

  const paramsObj = {
    areaId: blockId,
    orderBy: 'beginTime ASC',
    countType: 1,
    page: 1,
    pageSize: 1000,
    ...params,
  };

  return getPerfomanceApi({
    method: 'POST',
    pathname: `/batch/page`,
    body: formatQueryObjNew(paramsObj),
  });
}

function batchExtInfo() {
  const blockId = getQuery('blockId');
  const entry = getQuery('entry');

  const paramsObj = {
    areaId: blockId,
    orderBy: 'beginTime ASC',
    page: 1,
    pageSize: 1000,
    resourceAssignType: 1,
    allotPathType: Number(entry) === 0 ? 1 : 2,
  };

  return getPerfomanceApi({
    method: 'POST',
    pathname: `/batch/extInfo`,
    body: formatQueryObjNew(paramsObj),
  });
}

function contactsPage() {
  const paramsObj = {
    contactsType: 1,
    page: 1,
    pageSize: 1000,
  };

  return getPerfomanceApi({
    method: 'POST',
    pathname: `/contacts/page`,
    body: formatQueryObjNew(paramsObj),
  });
}

function batchIsInvited() {
  const blockId = getQuery('blockId');

  return getPerfomanceApi({
    method: 'GET',
    pathname: '/batch/isInvited',
    query: `?areaId=${blockId}`,
  });
}

// ----------报名页---------------
function areaDetailV2() {
  const activityId = getQuery('activityId');
  if (!activityId) return {};

  const blockId = getQuery('blockId') || '';

  return getPerfomanceApi({ pathname: `/area/detailV2`, query: `?activityId=${activityId}&areaId=${blockId}` });
}

function formsetAreaDetail(optType: 1 | 2) {
  const blockId = getQuery('blockId');
  if (!blockId || !optType) {
    console.error('入参不合法', blockId, optType);
    return {};
  }

  return getPerfomanceApi({
    method: 'POST',
    pathname: `/formset/area/detail`,
    body: `areaId=${blockId}&optType=${optType}`,
  });
}
// ----------报名页 END---------------

// ----------收品池列表-----------

function activityDetailV2(outerActivityId: number | string) {
  const activityId = outerActivityId || getQuery('activityId');
  if (!activityId) {
    console.error('入参不合法', activityId);
    return {};
  }

  return getPerfomanceApi({
    pathname: `/activity/detailV2`,
    query: `?activityId=${activityId}`,
  });
}

// 参与者
function areaPage() {
  const activityId = getQuery('activityId');

  if (!activityId) {
    console.error('入参不合法', activityId);
    return {};
  }

  const blockId = getQuery('blockId') || '';
  const applyValidStatus = getQuery('applyValidStatus') || '';

  return getPerfomanceApi({
    method: 'POST',
    pathname: `/area/page`,
    body: `activityId=${activityId}&page=1&pageSize=10&id=${blockId}&applyValidStatus=${applyValidStatus}`,
  });
}

// 管理者
function areaManagePage() {
  const activityId = getQuery('activityId');
  const receivingType = getQuery('receivingType');

  if (!activityId || !receivingType) {
    console.log('参数异常', activityId, receivingType);
    return {};
  }

  const blockId = getQuery('blockId') || '';

  return getPerfomanceApi({
    method: 'POST',
    pathname: `/area/manage/page`,
    body: `activityId=${activityId}&page=1&pageSize=10&id=${blockId}&viewCheckStatus=0&receivingType=${receivingType}`,
  });
}
// ----------管理者收品池列表 END-------

function applyPage() {
  const blockId = getQuery('blockId');
  const applyId = getQuery('applyId');
  const activityId = getQuery('activityId');
  const filterTab = getQuery('filterTab');
  const enrollId = getQuery('enrollId');
  const formInline = { activityInfo: '', applyInfo: '' };
  let actType = 'activityName';
  let applyType = 'applyId';
  let chargeMode: number | null = null;

  if (activityId) {
    formInline.activityInfo = activityId;
    actType = 'activityId';
  }

  if (blockId) {
    formInline.activityInfo = blockId;
    actType = 'areaId';
  }

  if (applyId || enrollId) {
    // @ts-ignore
    formInline.applyInfo = applyId || enrollId;
    applyType = 'applyId';
  }

  if (filterTab == '2') {
    chargeMode = 1;
  }

  const paramsObj: any = {
    [actType]: formInline.activityInfo,
    [applyType]: formInline.applyInfo,
    checkStatus: -1,
    syncStatus: -1,
    pageSize: 10,
    page: 1,
  };

  return getPerfomanceApi({
    method: 'POST',
    pathname: `/apply/page`,
    // 如果chargeMode是null则格式化为chargeMode，而不是chargeMode=
    body: formatQueryObjNew(paramsObj) + `&chargeMode${chargeMode == null ? '' : '=' + chargeMode}`,
  });
}

// 获取提报配置平台页面对应的页面Schema
function getTemplateSchema() {
  const pageId = getQuery('pageId');

  return getPerfomanceApi({
    method: 'GET',
    pathname: `/layout/template/schema/${pageId}`,
  });
}

export {
  areaDetailV2,
  formsetAreaDetail,
  areaPage,
  areaManagePage,
  // 不止用于性能优化
  activityDetailV2,
  usePerformanceApis,
  applyPage,
  batchPage,
  batchExtInfo,
  contactsPage,
  batchIsInvited,
  activityIsmanager,
  getTemplateSchema,
};
